import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PermissionGate from "../../../components/common/PermissionGate";
import hrService from "../../../api/services/hrService";
import {
  ArrowLeft,
  Edit,
  UserX,
  ArrowRight,
  Download,
  User,
  Building,
  FileText,
  CreditCard,
  Calendar,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Image,
  File,
  History,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { formatDate } from "../../../utils/stringUtils";
import { useAuth } from "../../../context/AuthContext";
import ExitRequestDetails from '../exit-management/ExitRequestDetails';
import exitService from '../../../api/services/exitService';
import { toast } from 'react-hot-toast';
import { queryClient } from "../../../lib/queryClient";

function formatSalaryAmountInr(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function formatMonthlyCtcInr(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') return '—';
  const n = parseInt(String(raw).replace(/\D/g, ''), 10);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

/** Read-only detail for one salary snapshot (current or history). */
function SalarySnapshotReadOnlyPanels({ snap }) {
  if (!snap || typeof snap !== 'object') return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0 rounded-lg bg-white/80 p-3 border border-gray-100">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Monthly CTC</p>
          <p className="text-sm font-semibold text-gray-900 mt-1">{formatMonthlyCtcInr(snap.monthlyCtc)}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg bg-white/80 p-3 border border-gray-100">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total earnings</p>
          <p className="text-sm font-semibold text-green-800 mt-1">
            {formatSalaryAmountInr(snap.totalEarnings)}
          </p>
        </div>
        <div className="rounded-lg bg-white/80 p-3 border border-gray-100">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total reimbursements</p>
          <p className="text-sm font-semibold text-blue-800 mt-1">
            {formatSalaryAmountInr(snap.totalReimbursements)}
          </p>
        </div>
        <div className="rounded-lg bg-white/80 p-3 border border-gray-100">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total deductions</p>
          <p className="text-sm font-semibold text-red-800 mt-1">
            {formatSalaryAmountInr(snap.totalDeductions)}
          </p>
        </div>
        <div className="rounded-lg bg-white/80 p-3 border border-gray-100">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Net pay</p>
          <p className="text-sm font-semibold text-agrosaf-green mt-1">
            {formatSalaryAmountInr(snap.netPay)}
          </p>
        </div>
      </div>
      {Array.isArray(snap.components) && snap.components.length > 0 && (
        <div className="overflow-x-auto -mx-1">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200 rounded-lg border border-gray-100 overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    % of CTC
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {snap.components.map((component, index) => (
                  <tr key={component.id || index} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-4 py-2.5 text-sm text-gray-900 capitalize break-words min-w-[120px]">
                      {component.name}
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${component.type === 'earning'
                            ? 'bg-green-100 text-green-800'
                            : component.type === 'deduction'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                      >
                        {component.type}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 text-sm text-gray-900 text-right whitespace-nowrap">
                      {component.type === 'reimbursement' ? '—' : `${component.percentageValue ?? ''}%`}
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                      {formatSalaryAmountInr(component.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {(snap.createdAt || snap.updatedAt) && (
        <div className="text-xs text-gray-500 pt-3 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-1">
          {snap.createdAt ? (
            <span>
              <span className="font-medium text-gray-600">Created</span>{' '}
              {formatDate(snap.createdAt, { includeTime: true }) || '—'}
            </span>
          ) : null}
          {snap.updatedAt ? (
            <span>
              <span className="font-medium text-gray-600">Updated</span>{' '}
              {formatDate(snap.updatedAt, { includeTime: true }) || '—'}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

const EmployeeProfile = ({ id, onClose, onEdit }) => {
  const navigate = useNavigate();
  const { employeeId: paramEmployeeId } = useParams();
  const employeeId = id || paramEmployeeId;
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [exitRequestId, setExitRequestId] = useState(null);

  // Helper to safely render address/location objects or strings
  const getLocationLabel = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      // Handle { id, name } or similar shapes
      return value.name || value.label || value.value || "";
    }
    return "";
  };

  // State management
  const [activeTab, setActiveTab] = useState("personal");
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'activate' or 'deactivate'
  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
  const [previewingDocument, setPreviewingDocument] = useState(null);
  const [employeeFullData, setEmployeeFullData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Tab data states
  const [personalData, setPersonalData] = useState(null);
  const [employmentData, setEmploymentData] = useState(null);
  const [documentsData, setDocumentsData] = useState([]);
  const [payrollData, setPayrollData] = useState(null);
  /** From GET `/hr/employees/:id/salary-structure` when the Salary tab is opened. */
  const [employeeSalaryStructure, setEmployeeSalaryStructure] = useState(null);
  const [salaryStructureLoading, setSalaryStructureLoading] = useState(false);
  const [salaryStructureError, setSalaryStructureError] = useState(null);
  /** Which salary history rows are expanded (`h-${storageOrderIndex}` keys from API). */
  const [expandedSalaryHistoryKeys, setExpandedSalaryHistoryKeys] = useState(() => new Set());
  const [leaveData, setLeaveData] = useState({ recentLeaves: [] });
  const [performanceData, setPerformanceData] = useState({
    goals: [],
    reviews: [],
  });
  // Transfer functionality commented out
  // const [transferHistory, setTransferHistory] = useState([]);

  // Mock employee data
  const mockEmployee = {
    id: employeeId || "EMP001",
    employeeId: employeeId || "EMP001",
    name: "John Smith",
    email: "john.smith@agrosaf.com",
    phone: "+91 98765 43210",
    department: "Sales",
    role: "Sales Manager",
    location: "Mumbai",
    doj: "2022-01-15",
    status: "Active",
    manager: "Sarah Johnson",
    avatar: null,
    personalInfo: {
      firstName: "John",
      lastName: "Smith",
      dateOfBirth: "1985-05-20",
      gender: "Male",
      maritalStatus: "Married",
      address: "123 Main Street, Mumbai, Maharashtra 400001",
      emergencyContact: "+91 98765 43211",
      emergencyContactName: "Jane Smith",
      bloodGroup: "O+",
      pan: "ABCDE1234F",
      aadhaar: "123456789012",
      uan: "123456789012",
      esi: "1234567890",
    },
    employmentInfo: {
      employeeId: "EMP001",
      department: "Sales",
      role: "Sales Manager",
      location: "Mumbai",
      dateOfJoining: "2022-01-15",
      employmentType: "Full-time",
      manager: "Sarah Johnson",
      workEmail: "john.smith@agrosaf.com",
      workPhone: "+91 22 1234 5678",
      reportingManager: "Sarah Johnson",
      teamSize: 5,
      probationPeriod: "6 months",
      confirmationDate: "2022-07-15",
    },
    documents: [
      {
        id: 1,
        name: "Resume.pdf",
        type: "Resume",
        status: "Approved",
        uploadedDate: "2022-01-10",
        size: "2.5 MB",
        uploadedBy: "John Smith",
      },
      {
        id: 2,
        name: "PAN_Card.pdf",
        type: "ID Proof",
        status: "Approved",
        uploadedDate: "2022-01-10",
        size: "1.2 MB",
        uploadedBy: "John Smith",
      },
      {
        id: 3,
        name: "Aadhaar_Card.pdf",
        type: "ID Proof",
        status: "Pending",
        uploadedDate: "2022-01-12",
        size: "1.8 MB",
        uploadedBy: "John Smith",
      },
      {
        id: 4,
        name: "Offer_Letter.pdf",
        type: "Employment",
        status: "Approved",
        uploadedDate: "2022-01-08",
        size: "3.1 MB",
        uploadedBy: "HR Team",
      },
    ],
    payroll: {
      basicSalary: 50000,
      hra: 15000,
      medicalAllowance: 2000,
      transportAllowance: 3000,
      totalEarnings: 70000,
      pf: 6000,
      esi: 1050,
      professionalTax: 200,
      incomeTax: 5000,
      totalDeductions: 12250,
      netSalary: 57750,
      bankAccount: "1234567890123456",
      ifsc: "HDFC0001234",
      bankName: "HDFC Bank",
      payFrequency: "Monthly",
      lastPayDate: "2024-01-31",
    },
    leaveAttendance: {
      totalLeaves: 21,
      usedLeaves: 8,
      remainingLeaves: 13,
      sickLeaves: 2,
      casualLeaves: 4,
      earnedLeaves: 2,
      attendance: {
        present: 22,
        absent: 1,
        late: 3,
        earlyLeave: 1,
      },
      recentLeaves: [
        {
          id: 1,
          type: "Sick Leave",
          startDate: "2024-01-15",
          endDate: "2024-01-16",
          days: 2,
          status: "Approved",
          reason: "Fever",
        },
        {
          id: 2,
          type: "Casual Leave",
          startDate: "2024-01-20",
          endDate: "2024-01-20",
          days: 1,
          status: "Approved",
          reason: "Personal work",
        },
      ],
    },
    performance: {
      overallRating: 4.2,
      lastReviewDate: "2023-12-15",
      nextReviewDate: "2024-06-15",
      goals: [
        {
          id: 1,
          title: "Increase Sales by 20%",
          target: "20%",
          achieved: "18%",
          status: "In Progress",
          dueDate: "2024-03-31",
        },
        {
          id: 2,
          title: "Complete Leadership Training",
          target: "100%",
          achieved: "75%",
          status: "In Progress",
          dueDate: "2024-02-28",
        },
      ],
      reviews: [
        {
          id: 1,
          period: "Q4 2023",
          rating: 4.2,
          reviewer: "Sarah Johnson",
          comments: "Excellent performance in sales targets",
          date: "2023-12-15",
        },
      ],
    },
  };

  // Load employee data
  useEffect(() => {
    loadEmployeeData();
  }, [employeeId]);

  // Check for tab parameter in URL and set active tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    // Transfer functionality commented out - removed 'transferHistory' from tabParam check
    if (tabParam && ['personal', 'employment', 'documents', 'payroll', 'exit-request'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    setExpandedSalaryHistoryKeys(new Set());
    setEmployeeSalaryStructure(null);
    setSalaryStructureError(null);
    setSalaryStructureLoading(false);
  }, [employeeId]);

  useEffect(() => {
    if (activeTab !== 'payroll' || !employeeId) {
      return undefined;
    }
    let cancelled = false;
    setSalaryStructureLoading(true);
    setSalaryStructureError(null);
    hrService
      .getEmployeeSalaryStructure(employeeId)
      .then((result) => {
        if (cancelled) return;
        setSalaryStructureLoading(false);
        if (result.success && result.data?.currentStructure) {
          setEmployeeSalaryStructure(result.data);
        } else if (result.success) {
          setEmployeeSalaryStructure(null);
        } else {
          setEmployeeSalaryStructure(null);
          setSalaryStructureError(result.error || 'Failed to load salary structure');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSalaryStructureLoading(false);
        setEmployeeSalaryStructure(null);
        setSalaryStructureError('Failed to load salary structure');
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, employeeId]);

  const loadEmployeeData = async () => {
    setLoading(true);
    setError(null);
    setExitRequestId(null);

    try {


      // Call the actual API
      const result = await hrService.getEmployeeById(employeeId);


      if (result.success) {
        const emp = result.data;


        // Transform API data to match the UI structure
        const employeeData = {
          id: emp._id,
          employeeId: emp.employeeId,
          name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase()),
          email: emp.user?.email || emp.contactInfo?.personalEmail || '',
          phone: emp.user?.phone || emp.contactInfo?.personalPhone || '',
          officialEmail: emp.user?.email || '',
          officialPhone: emp.user?.phone || '',
          personalEmail: emp.contactInfo?.personalEmail || '',
          personalPhone: emp.contactInfo?.personalPhone || '',
          department: emp.employmentInfo?.department || '',
          role: emp.employmentInfo?.position || '',
          location: getLocationLabel(emp.employmentInfo?.workLocation) || '',
          doj: emp.employmentInfo?.joinDate ? new Date(emp.employmentInfo.joinDate).toISOString().split('T')[0] : '',
          status: emp.employmentInfo?.status === 'active' ? 'Active' :
            emp.employmentInfo?.status === 'inactive' ? 'Inactive' :
              emp.employmentInfo?.status === 'exited' ? 'Exited' :
                emp.employmentInfo?.status === 'transferred' ? 'Transferred' :
                  emp.employmentInfo?.status === 'on leave' ? 'On Leave' :
                    (emp.employmentInfo?.status || 'Active').replace(/\b\w/g, l => l.toUpperCase()),
          manager: emp.employmentInfo?.reportingManager ?
            `${emp.employmentInfo.reportingManager.personalInfo?.firstName || ''} ${emp.employmentInfo.reportingManager.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase()) :
            'Not Assigned',
          avatar: emp.systemInfo?.profilePicture || null,
          personalInfo: {
            firstName: (emp.personalInfo?.firstName || '').replace(/\b\w/g, l => l.toUpperCase()),
            lastName: (emp.personalInfo?.lastName || '').replace(/\b\w/g, l => l.toUpperCase()),
            dateOfBirth: emp.personalInfo?.dateOfBirth || '',
            gender: emp.personalInfo?.gender || '',
            maritalStatus: emp.personalInfo?.maritalStatus || '',
            address: getLocationLabel(emp.contactInfo?.currentAddress?.city) || '',
            emergencyContact: emp.contactInfo?.emergencyContact || '',
            emergencyContactName: (emp.contactInfo?.emergencyContactName || '').replace(/\b\w/g, l => l.toUpperCase()),
            bloodGroup: emp.personalInfo?.bloodGroup || '',
            personalEmail: emp.contactInfo?.personalEmail || '',
            personalPhone: emp.contactInfo?.personalPhone || '',
            pan: emp.complianceInfo?.pan || '',
            aadhaar: emp.complianceInfo?.aadhaar || '',
            uan: emp.complianceInfo?.uan || '',
            esi: emp.complianceInfo?.esi || ''
          },
          employmentInfo: {
            employeeId: emp.employeeId,
            department: emp.employmentInfo?.department || '',
            role: emp.employmentInfo?.position || '',
            designation: emp.employmentInfo?.designation || '',
            location: getLocationLabel(emp.employmentInfo?.workLocation) || '',
            dateOfJoining: emp.employmentInfo?.joinDate ? new Date(emp.employmentInfo.joinDate).toISOString().split('T')[0] : '',
            employmentType: emp.employmentInfo?.employmentType || 'Full-time',
            manager: emp.employmentInfo?.reportingManager ?
              `${emp.employmentInfo.reportingManager.personalInfo?.firstName || ''} ${emp.employmentInfo.reportingManager.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase()) :
              'Not Assigned',
            shift: emp.employmentInfo?.shift ?
              `${emp.employmentInfo.shift.name || 'N/A'} (${emp.employmentInfo.shift.startTime || ''} - ${emp.employmentInfo.shift.endTime || ''})` :
              'Not Assigned',
            workEmail: emp.user?.email || '',
            workPhone: emp.user?.phone || '',
            reportingManager: emp.employmentInfo?.reportingManager ?
              `${emp.employmentInfo.reportingManager.personalInfo?.firstName || ''} ${emp.employmentInfo.reportingManager.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase()) :
              'Not Assigned',
            teamSize: 5,
            probationPeriod: "6 months",
            confirmationDate: emp.employmentInfo?.joinDate
              ? new Date(emp.employmentInfo.joinDate)
                .toISOString()
                .split("T")[0]
              : "",
          },
          documents: emp.documents || [],
          payroll: {
            bankAccount: emp.complianceInfo?.bankAccount || "",
            ifsc: emp.complianceInfo?.ifsc || "",
            bankName: emp.complianceInfo?.bankName || "",
            bankBranchName: emp.complianceInfo?.bankBranchName || "",
          },
          leaveAttendance: emp.leaveAttendance || {},
          performance: emp.performance || {},
        };

        setEmployee(employeeData);
        setPersonalData(employeeData.personalInfo);
        setEmploymentData(employeeData.employmentInfo);
        setPayrollData(employeeData.payroll);

        // Store full employee data for document preview
        setEmployeeFullData(emp);

        // Transfer functionality commented out
        // Extract and set transfer history
        // setTransferHistory(emp.transferHistory || []);

        // Extract and flatten documents from nested structure
        const allDocuments = [];
        if (emp.documents) {
          // Extract resume documents
          if (Array.isArray(emp.documents.resume)) {
            emp.documents.resume.forEach(doc => {
              allDocuments.push({
                ...doc,
                id: doc._id || doc.id,
                type: 'Resume',
                uploadedDate: doc.uploadedAt || doc.uploadedDate,
                uploadedAt: doc.uploadedAt || doc.uploadedDate,
                status: doc.status || 'available',
                name: doc.name || 'Resume'
              });
            });
          }
          // Extract ID proof documents
          if (Array.isArray(emp.documents.idProof)) {
            emp.documents.idProof.forEach(doc => {
              allDocuments.push({
                ...doc,
                id: doc._id || doc.id,
                type: 'ID Proof',
                uploadedDate: doc.uploadedAt || doc.uploadedDate,
                uploadedAt: doc.uploadedAt || doc.uploadedDate,
                status: doc.status || 'available',
                name: doc.name || 'ID Proof'
              });
            });
          }
          // Extract other documents
          if (Array.isArray(emp.documents.otherDocuments)) {
            emp.documents.otherDocuments.forEach(doc => {
              allDocuments.push({
                ...doc,
                id: doc._id || doc.id,
                type: 'Other',
                uploadedDate: doc.uploadedAt || doc.uploadedDate,
                uploadedAt: doc.uploadedAt || doc.uploadedDate,
                status: doc.status || 'available',
                name: doc.name || 'Other Document'
              });
            });
          }
        }
        setDocumentsData(allDocuments);

        // Only load from separate API if no documents found in main response
        if (allDocuments.length === 0) {
          await loadDocumentsData();
        }

        // Check if employee has submitted an exit request from core API data
        if (result.data.hasExitRequest || result.data.exitRequestId) {
          setExitRequestId(result.data.exitRequestId);
        }

        // Load other data
        await loadLeaveData();
        await loadAttendanceData();
        await loadPerformanceData();
      } else {
        setError(result.error || "Employee not found");
      }
    } catch (err) {
      setError("Failed to load employee data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load documents data
  const loadDocumentsData = async () => {
    try {
      const result = await hrService.getEmployeeDocuments(employeeId);
      if (result.success && result.data && result.data.length > 0) {
        // Process documents to ensure they have name field
        const processedDocs = (result.data || []).map(doc => ({
          ...doc,
          id: doc._id || doc.id,
          name: doc.name || doc.originalName || doc.filename || 'Document',
          uploadedDate: doc.uploadedAt || doc.uploadedDate,
          uploadedAt: doc.uploadedAt || doc.uploadedDate,
          status: doc.status || 'available'
        }));
        setDocumentsData(processedDocs);
      }
    } catch (error) {

      // Don't clear documents if API fails
    }
  };

  // Load payroll data
  const loadPayrollData = async () => {
    try {
      const result = await hrService.getEmployeePayroll(employeeId);
      if (result.success) {
        setPayrollData(result.data);
      }
    } catch (error) {

    }
  };

  // Load leave data
  const loadLeaveData = async () => {
    try {
      const result = await hrService.getEmployeeLeaves(employeeId);
      if (result.success) {
        // Calculate leave data from API response
        const leaveData = {
          totalLeaves: result.data.totalLeaves || 21,
          usedLeaves: result.data.usedLeaves || 0,
          remainingLeaves: (result.data.totalLeaves || 21) - (result.data.usedLeaves || 0),
          recentLeaves: result.data.recentLeaves || [],
          leaveTypes: result.data.leaveTypes || [],
        };
        setLeaveData(leaveData);
      } else {
        // Use fallback data if API fails
        setLeaveData({
          totalLeaves: 21,
          usedLeaves: 0,
          remainingLeaves: 21,
          recentLeaves: []
        });
      }
    } catch (error) {

      setLeaveData({
        totalLeaves: 21,
        usedLeaves: 0,
        remainingLeaves: 21,
        recentLeaves: []
      });
    }
  };

  // Load attendance data
  const loadAttendanceData = async () => {
    try {
      const result = await hrService.getEmployeeAttendance(employeeId);
      if (result.success) {
        // Calculate attendance data from API response
        const attendanceData = {
          presentDays: result.data.presentDays || 0,
          absentDays: result.data.absentDays || 0,
          lateDays: result.data.lateDays || 0,
          earlyLeaveDays: result.data.earlyLeaveDays || 0,
          totalWorkingDays: result.data.totalWorkingDays || 0,
          attendancePercentage: result.data.attendancePercentage || 0
        };

        // Update leaveData with attendance
        setLeaveData(prev => ({ ...prev, attendance: attendanceData }));
      } else {
        // Use fallback data if API fails
        const fallbackAttendance = {
          presentDays: 0,
          absentDays: 0,
          lateDays: 0,
          earlyLeaveDays: 0,
          totalWorkingDays: 0,
          attendancePercentage: 0
        };
        setLeaveData(prev => ({ ...prev, attendance: fallbackAttendance }));
      }
    } catch (error) {

      const fallbackAttendance = {
        presentDays: 0,
        absentDays: 0,
        lateDays: 0,
        earlyLeaveDays: 0,
        totalWorkingDays: 0,
        attendancePercentage: 0
      };
      setLeaveData(prev => ({ ...prev, attendance: fallbackAttendance }));
    }
  };

  // Load performance data
  const loadPerformanceData = async () => {
    try {
      const result = await hrService.getEmployeePerformance(employeeId);
      if (result.success) {
        // Transform API data to match UI structure
        const performanceData = {
          goals: result.data.goals || [],
          reviews: result.data.reviews || [],
          rating: result.data.rating || 0,
          lastReviewDate: result.data.lastReviewDate || "",
          nextReviewDate: result.data.nextReviewDate || "",
          achievements: result.data.achievements || [],
          areasForImprovement: result.data.areasForImprovement || [],
        };
        setPerformanceData(performanceData);
      } else {
        // Use fallback data if API fails
        setPerformanceData({
          goals: [],
          reviews: [],
          rating: 0,
          lastReviewDate: '',
          nextReviewDate: '',
          achievements: [],
          areasForImprovement: []
        });
      }
    } catch (error) {

      setPerformanceData({
        goals: [],
        reviews: [],
        rating: 0,
        lastReviewDate: '',
        nextReviewDate: '',
        achievements: [],
        areasForImprovement: []
      });
    }
  };

  // Tab configuration
  const tabs = [
    { id: "personal", label: "Personal", icon: User },
    { id: "employment", label: "Employment", icon: Building },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "payroll", label: "Salary", icon: CreditCard },
    ...(exitRequestId ? [{ id: "exit-request", label: "Exit request", icon: UserX }] : []),
  ];

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // Handle quick actions
  const handleEditEmployee = () => {
    if (onEdit) {
      onEdit();
      return;
    }
    navigate(`/edit_employee/${employeeId}`);
  };

  const openConfirm = (type) => {
    setActionType(type);
    setShowConfirmModal(true);
  };

  const closeConfirm = () => {
    setShowConfirmModal(false);
    setActionType('');
  };

  const handleDeactivateEmployeeConfirmed = async () => {
    try {
      setLoading(true);

      const result = await hrService.updateEmployee(employeeId, { status: 'inactive' });

      if (result.success) {

        alert('Employee deactivated successfully!\n\nThe employee has been marked as inactive and their system access has been revoked. All their data has been preserved.');
        await loadEmployeeData();
      } else {

        alert(`Failed to deactivate employee: ${result.error}`);
      }
    } catch (error) {

      alert('Failed to deactivate employee. Please try again.');
    } finally {
      setLoading(false);
      closeConfirm();
    }
  };

  const handleActivateEmployeeConfirmed = async () => {
    try {
      setLoading(true);

      const result = await hrService.updateEmployee(employeeId, { status: 'active' });

      if (result.success) {

        alert('Employee activated successfully!\n\nThe employee has been marked as active and their system access has been restored.');
        await loadEmployeeData();
      } else {

        alert(`Failed to activate employee: ${result.error}`);
      }
    } catch (error) {

      alert('Failed to activate employee. Please try again.');
    } finally {
      setLoading(false);
      closeConfirm();
    }
  };

  // Document action handlers
  const handleViewDocument = (doc) => {


    // Ensure we have all necessary fields for preview
    const documentForPreview = {
      ...doc,
      // Ensure fileUrl is set from url if fileUrl is missing
      fileUrl: doc.fileUrl || (doc.url && doc.url !== "#" ? doc.url : null),
      // Ensure _id is available
      _id: doc._id || doc.id,
      // Ensure filename is available
      filename: doc.filename || doc.name
    };



    // Set the document to preview and show preview modal
    setPreviewingDocument(documentForPreview);
    setShowDocumentPreviewModal(true);
  };

  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'document').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || '').toLowerCase();
    if (type.includes('pdf')) return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type.includes('png')) return `${clean}.png`;
    if (type.includes('jpeg') || type.includes('jpg')) return `${clean}.jpg`;
    if (type.includes('gif')) return `${clean}.gif`;
    if (type.includes('webp')) return `${clean}.webp`;
    return clean;
  };

  const getFilenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const lastPart = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-zA-Z0-9]+$/.test(lastPart) ? lastPart : '';
    } catch (error) {
      return '';
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadDocument = async (doc) => {
    try {


      // Get fileUrl from doc (prioritize fileUrl, then url)
      let fileUrl = doc.fileUrl || (doc.url && doc.url !== "#" ? doc.url : null);

      // Check if documentData exists and parse it to extract fileUrl
      if (!fileUrl && doc.documentData) {
        try {
          let documentDataParsed;
          if (typeof doc.documentData === 'string') {
            documentDataParsed = JSON.parse(doc.documentData);
          } else {
            documentDataParsed = doc.documentData;
          }

          if (documentDataParsed && documentDataParsed.fileUrl) {
            fileUrl = documentDataParsed.fileUrl;

          }
        } catch (parseError) {

        }
      }

      // Handle base64 data URLs directly in the frontend
      if (fileUrl && fileUrl.startsWith('data:')) {

        try {
          // Parse data URL: data:image/png;base64,iVBORw0KGgo...
          const matches = fileUrl.match(/^data:([^;]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            const mimeType = matches[1];
            const base64Data = matches[2];

            // Convert base64 to blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Determine file extension from mime type
            let extension = '';
            if (mimeType.includes('png')) extension = '.png';
            else if (mimeType.includes('jpeg') || mimeType.includes('jpg')) extension = '.jpg';
            else if (mimeType.includes('pdf')) extension = '.pdf';
            else if (mimeType.includes('gif')) extension = '.gif';
            else if (mimeType.includes('webp')) extension = '.webp';

            // Get filename
            const filename = (doc.name || doc.originalName || doc.filename || 'document') +
              (extension && !(doc.name || doc.originalName || doc.filename || '').includes('.') ? extension : '');

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);


            return;
          }
        } catch (base64Error) {

          // Fall through to API download
        }
      }

      // If document has a direct fileUrl (Cloudinary or other HTTP URL), download it directly
      if (fileUrl && (fileUrl.includes('cloudinary.com') || fileUrl.startsWith('http'))) {


        const preferredName =
          doc.originalName ||
          doc.filename ||
          doc.name ||
          getFilenameFromUrl(fileUrl) ||
          'document';
        const resolvedFilename = ensureFilenameHasExtension(preferredName, doc.fileType);

        try {
          const response = await authFetch(fileUrl);
          if (!response.ok) throw new Error('Failed to fetch file');
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const finalFilename = ensureFilenameHasExtension(
            resolvedFilename,
            blob.type || doc.fileType
          );
          triggerBrowserDownload(blobUrl, finalFilename);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (directFetchError) {
          // Fallback for CORS-restricted file URLs.
          triggerBrowserDownload(fileUrl, resolvedFilename);
        }


        return;
      }

      // Otherwise, use the API download endpoint
      // Priority: MongoDB _id > filename > id (which might be in format resume_0)
      let documentId = doc._id || doc.filename || doc.id;



      if (!documentId) {

        alert('Document ID not found. Cannot download document.');
        return;
      }

      // If documentId is a string like "resume_0", pass it as-is (backend will handle it)
      // Otherwise, convert ObjectId to string
      if (documentId && typeof documentId !== 'string') {
        documentId = documentId.toString();
      }



      const result = await hrService.downloadEmployeeDocument(employeeId, documentId);

      if (result.success) {

      } else {

        alert(result.error || 'Failed to download document. Please try again.');
      }
    } catch (error) {

      alert('Failed to download document. Please try again.');
    }
  };



  const handleExportProfile = async () => {
    if (isExporting || !employeeId) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName =
        employee?.name ||
        employee?.employeeId ||
        'Employee';
      const result = await hrService.exportEmployeeDetail(employeeId, exportName);
      if (result.success) {
        toast.success('Employee profile exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export employee profile', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to export employee profile', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "transferred":
        return "bg-blue-100 text-blue-800";
      case "on leave":
        return "bg-yellow-100 text-yellow-800";
      case "exited":
        return "bg-purple-100 text-purple-800";
      case "approved":
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get file icon based on file type
  const getFileIcon = (fileType, fileName) => {
    if (!fileType && !fileName) return FileText;

    // Get extension from fileName if available
    let extension = '';
    if (fileName) {
      const match = fileName.toLowerCase().match(/\.([^.]+)$/);
      if (match) extension = match[1];
    }
    if (!extension && fileType) {
      extension = fileType.toLowerCase();
    }

    // Image types
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'image'].includes(extension)) {
      return Image;
    }

    // PDF
    if (extension === 'pdf') {
      return FileText;
    }

    // Office documents
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'].includes(extension)) {
      return File;
    }

    // Video
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      return File;
    }

    // Audio
    if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) {
      return File;
    }

    // Text files
    if (['txt', 'csv', 'json', 'xml', 'html', 'css', 'js', 'md', 'log'].includes(extension)) {
      return FileText;
    }

    return File;
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'bg-green-100 text-green-800'
        };
      case 'pending':
        return {
          icon: Clock,
          color: 'bg-yellow-100 text-yellow-800'
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'bg-red-100 text-red-800'
        };
      default:
        return {
          icon: Clock,
          color: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-800 mb-2">
          Error Loading Employee
        </h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadEmployeeData}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Employee Not Found
        </h3>
        <p className="text-gray-600">
          The requested employee could not be found.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden">
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black opacity-40"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-4 sm:p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 mr-2 flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">Confirm {actionType === 'deactivate' ? 'Deactivation' : 'Activation'}</h3>
            </div>
            <p className="text-sm sm:text-base text-gray-700 mb-6 break-words">
              {actionType === 'deactivate'
                ? 'Are you sure you want to deactivate this employee? This will revoke system access but preserve all data.'
                : 'Are you sure you want to activate this employee? This will restore system access.'}
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-0">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm sm:text-base w-full sm:w-auto"
              >
                Cancel
              </button>
              <PermissionGate permission={actionType === 'deactivate' ? "employee_management" : "employee_management"} action={actionType === 'deactivate' ? "delete" : "update"}>
                <button
                  onClick={actionType === 'deactivate' ? handleDeactivateEmployeeConfirmed : handleActivateEmployeeConfirmed}
                  className={`px-4 py-2 rounded-md text-white text-sm sm:text-base w-full sm:w-auto ${actionType === 'deactivate' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  Yes, {actionType === 'deactivate' ? 'Deactivate' : 'Activate'}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <button
            onClick={() => onClose ? onClose() : navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Employee Profile
            </h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base truncate">
              View and manage employee information
            </p>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          <PermissionGate permission="employee_management" action="update">
            <button
              onClick={handleEditEmployee}
              className="flex items-center px-3 sm:px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors text-sm sm:text-base"
            >
              <Edit className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          </PermissionGate>
          {employee?.status === 'Active' ? (
            <PermissionGate permission="employee_management" action="delete">
              <button
                onClick={() => openConfirm('deactivate')}
                className="flex items-center px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
              >
                <UserX className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Deactivate</span>
              </button>
            </PermissionGate>
          ) : (
            <PermissionGate permission="employee_management" action="update">
              <button
                onClick={() => openConfirm('activate')}
                className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
              >
                <CheckCircle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Activate</span>
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="employee_management" action="read">
            <button
              onClick={handleExportProfile}
              disabled={isExporting}
              className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className={`h-4 w-4 ${isExporting ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Export'}</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Employee Summary Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="flex-shrink-0 mx-auto sm:mx-0">
            {employee.avatar ? (
              <img
                src={employee.avatar}
                alt={employee.name}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-agrosaf-green rounded-full flex items-center justify-center">
                <User className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 w-full">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                  {employee.name}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 break-words">{employee.role}</p>
                <p className="text-sm sm:text-base text-gray-500 capitalize break-words">
                  {employee.department} • {employee.location}
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-2 space-y-2 sm:space-y-0">
                  <span className="flex items-start sm:items-center text-sm text-gray-600 min-w-0 flex-1 sm:flex-initial">
                    <Mail className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="break-words break-all">{employee.email}</span>
                  </span>
                  <span className="flex items-center text-sm text-gray-600 whitespace-nowrap">
                    <Phone className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span>{employee.phone}</span>
                  </span>
                </div>
              </div>
              <div className="text-left sm:text-right flex-shrink-0">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(
                    employee.status
                  )}`}
                >
                  {employee.status}
                </span>
                <p className="text-sm text-gray-500 mt-1 break-words">
                  Employee ID: {employee.employeeId}
                </p>
                <p className="text-sm text-gray-500 break-words">DOJ: {formatDate(employee.doj, { includeTime: false })}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 min-w-max sm:min-w-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${activeTab === tab.id
                    ? "border-agrosaf-green text-agrosaf-green"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6 overflow-x-auto">
          {activeTab === "personal" && personalData && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {personalData.firstName} {personalData.lastName}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {formatDate(personalData?.dateOfBirth, { includeTime: false }) || personalData?.dateOfBirth || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Gender
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                    {personalData.gender}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Personal Email
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {personalData.personalEmail || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Personal Mobile
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {personalData.personalPhone || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    PAN Number
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {personalData.pan}
                  </p>
                </div>
                <div className="md:col-span-2 min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Current Address
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                    {[
                      employeeFullData?.contactInfo?.currentAddress?.street,
                      getLocationLabel(employeeFullData?.contactInfo?.currentAddress?.city),
                      getLocationLabel(employeeFullData?.contactInfo?.currentAddress?.state),
                      employeeFullData?.contactInfo?.currentAddress?.pincode,
                      getLocationLabel(employeeFullData?.contactInfo?.currentAddress?.country),
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </p>
                </div>
                <div className="md:col-span-2 min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Permanent Address
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                    {[
                      employeeFullData?.contactInfo?.permanentAddress?.street,
                      getLocationLabel(employeeFullData?.contactInfo?.permanentAddress?.city),
                      getLocationLabel(employeeFullData?.contactInfo?.permanentAddress?.state),
                      employeeFullData?.contactInfo?.permanentAddress?.pincode,
                      getLocationLabel(employeeFullData?.contactInfo?.permanentAddress?.country),
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "employment" && employmentData && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Employment Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Employee ID
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.employeeId}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.department}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.role}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Designation
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.designation || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.location}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Date of Joining
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {formatDate(employmentData?.dateOfJoining, { includeTime: false }) || employmentData?.dateOfJoining || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Employment Type
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                    {employmentData.employmentType}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Manager
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.manager}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Official Email
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.workEmail || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Official Mobile
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.workPhone || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Shift
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {employmentData.shift}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Confirmation Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-words">
                    {formatDate(employmentData?.confirmationDate, { includeTime: false }) || employmentData?.confirmationDate || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Documents
                </h3>
              </div>
              <div className="space-y-4">
                {documentsData && documentsData.length > 0 ? (
                  documentsData.map((doc) => (
                    <div key={doc.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors gap-3">
                      <div className="flex items-center space-x-4 flex-1 min-w-0 w-full sm:w-auto">
                        <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 break-words capitalize">{doc.name || doc.originalName || 'Document'}</p>
                          {doc.originalName && doc.name && doc.originalName !== doc.name && (
                            <p className="text-xs text-gray-400 break-words mt-0.5">{doc.originalName}</p>
                          )}
                          <p className="text-sm text-gray-500 truncate">
                            {doc.type} {doc.type === 'Compliance' ? '••' : '•'} {formatDate(doc.uploadedDate || doc.uploadedAt, { includeTime: false }) || doc.uploadedDate || doc.uploadedAt || ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0 w-full sm:w-auto justify-end">
                        <PermissionGate permission="employee_management" action="read">
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Found</h3>
                    <p className="text-gray-600">No documents have been uploaded for this employee yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "payroll" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Salary
              </h3>

              {salaryStructureLoading && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
                  Loading salary structure…
                </div>
              )}

              {!salaryStructureLoading && salaryStructureError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {salaryStructureError}
                </div>
              )}

              {!salaryStructureLoading && !salaryStructureError && employeeSalaryStructure?.currentStructure ? (
                <>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">
                      Current salary structure
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Monthly CTC
                        </label>
                        <p className="text-lg font-semibold text-gray-900 break-words">
                          {formatMonthlyCtcInr(employeeSalaryStructure.currentStructure.monthlyCtc)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total earnings</p>
                        <p className="text-sm font-semibold text-green-800 mt-1">
                          {formatSalaryAmountInr(employeeSalaryStructure.currentStructure.totalEarnings)}
                        </p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total reimbursements</p>
                        <p className="text-sm font-semibold text-blue-800 mt-1">
                          {formatSalaryAmountInr(employeeSalaryStructure.currentStructure.totalReimbursements)}
                        </p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total deductions</p>
                        <p className="text-sm font-semibold text-red-800 mt-1">
                          {formatSalaryAmountInr(employeeSalaryStructure.currentStructure.totalDeductions)}
                        </p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Net pay</p>
                        <p className="text-sm font-semibold text-agrosaf-green mt-1">
                          {formatSalaryAmountInr(employeeSalaryStructure.currentStructure.netPay)}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Net pay is stored as total earnings plus total reimbursements.
                    </p>
                    {employeeSalaryStructure.currentStructure.justification && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Justification</p>
                        <p className="text-sm text-gray-800 break-words whitespace-pre-wrap first-letter:capitalize">
                          {employeeSalaryStructure.currentStructure.justification}
                        </p>
                      </div>
                    )}
                    {(employeeSalaryStructure.currentStructure.createdAt ||
                      employeeSalaryStructure.currentStructure.updatedAt) && (
                        <div className="text-xs text-gray-600 mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-1">
                          {employeeSalaryStructure.currentStructure.createdAt ? (
                            <span>
                              <span className="font-medium text-gray-700">Created</span>{' '}
                              {formatDate(employeeSalaryStructure.currentStructure.createdAt, {
                                includeTime: true,
                              }) || '—'}
                            </span>
                          ) : null}
                          {employeeSalaryStructure.currentStructure.updatedAt ? (
                            <span>
                              <span className="font-medium text-gray-700">Updated</span>{' '}
                              {formatDate(employeeSalaryStructure.currentStructure.updatedAt, {
                                includeTime: true,
                              }) || '—'}
                            </span>
                          ) : null}
                        </div>
                      )}
                  </div>

                  {Array.isArray(employeeSalaryStructure.currentStructure.components) &&
                    employeeSalaryStructure.currentStructure.components.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <h4 className="text-md font-semibold text-gray-900 mb-4">
                          Components
                        </h4>
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                          <div className="inline-block min-w-full align-middle">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Name
                                  </th>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Type
                                  </th>
                                  <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    % of CTC
                                  </th>
                                  <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Amount
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {employeeSalaryStructure.currentStructure.components.map((component, index) => (
                                  <tr key={component.id || index} className="hover:bg-gray-50">
                                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-900 capitalize break-words min-w-[120px]">
                                      {component.name}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 text-sm whitespace-nowrap">
                                      <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${component.type === 'earning'
                                            ? 'bg-green-100 text-green-800'
                                            : component.type === 'deduction'
                                              ? 'bg-red-100 text-red-800'
                                              : 'bg-blue-100 text-blue-800'
                                          }`}
                                      >
                                        {component.type}
                                      </span>
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                                      {component.type === 'reimbursement'
                                        ? '—'
                                        : `${component.percentageValue ?? ''}%`}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                                      {formatSalaryAmountInr(component.value)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                  {Array.isArray(employeeSalaryStructure.structureHistory) &&
                    employeeSalaryStructure.structureHistory.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <h4 className="text-md font-semibold text-gray-900 mb-1 flex items-center gap-2">
                          <History className="h-4 w-4 text-gray-600" />
                          Previous structures
                        </h4>
                        <p className="text-xs text-gray-500 mb-4">
                          Order matches the server response (no client-side sorting).
                        </p>
                        <div className="space-y-2">
                          {employeeSalaryStructure.structureHistory.map((snap, idx) => {
                            const n = employeeSalaryStructure.structureHistory.length;
                            const storageIdx =
                              snap.storageOrderIndex !== undefined && snap.storageOrderIndex !== null
                                ? snap.storageOrderIndex
                                : idx;
                            const rowKey = `h-${storageIdx}`;
                            const isOpen = expandedSalaryHistoryKeys.has(rowKey);
                            const versionNumber =
                              snap.storageOrderIndex !== undefined && snap.storageOrderIndex !== null
                                ? snap.storageOrderIndex + 1
                                : idx + 1;
                            const snapForPanels = { ...snap };
                            delete snapForPanels.storageOrderIndex;
                            return (
                              <div
                                key={rowKey}
                                className="border border-gray-100 rounded-lg bg-gray-50/80 overflow-hidden"
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setExpandedSalaryHistoryKeys((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(rowKey)) next.delete(rowKey);
                                      else next.add(rowKey);
                                      return next;
                                    });
                                  }}
                                  className="w-full flex items-start gap-3 p-3 sm:p-3.5 text-left hover:bg-gray-100/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-agrosaf-green focus-visible:ring-inset"
                                  aria-expanded={isOpen}
                                >
                                  <span className="text-gray-500 shrink-0 mt-0.5" aria-hidden>
                                    {isOpen ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-baseline justify-between gap-2 gap-y-1">
                                      <p className="text-xs font-medium text-gray-500">
                                        Version {versionNumber}
                                        {n === 1 ? (
                                          <span className="text-gray-400 font-normal"> (previous structure)</span>
                                        ) : (
                                          <>
                                            {idx === 0 ? (
                                              <span className="text-gray-400 font-normal"> (most recent)</span>
                                            ) : null}
                                            {idx === n - 1 ? (
                                              <span className="text-gray-400 font-normal"> (oldest)</span>
                                            ) : null}
                                          </>
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-700">
                                        <span className="text-gray-500">Monthly CTC </span>
                                        <span className="font-semibold text-gray-900 tabular-nums">
                                          {formatMonthlyCtcInr(snap.monthlyCtc)}
                                        </span>
                                      </p>
                                    </div>
                                    {(snap.createdAt || snap.updatedAt) && (
                                      <p className="text-[10px] text-gray-500 mt-2 w-full leading-relaxed">
                                        {snap.createdAt ? (
                                          <>Created {formatDate(snap.createdAt, { includeTime: true })}</>
                                        ) : null}
                                        {snap.createdAt && snap.updatedAt ? ' · ' : null}
                                        {snap.updatedAt ? (
                                          <>Updated {formatDate(snap.updatedAt, { includeTime: true })}</>
                                        ) : null}
                                      </p>
                                    )}
                                    {snap.justification && (
                                      <div className="mt-2 text-xs text-gray-700 border-l-2 border-gray-200 pl-2">
                                        <span className="font-medium text-gray-500">Justification:</span>{' '}
                                        <span className="break-words whitespace-pre-wrap">{snap.justification}</span>
                                      </div>
                                    )}
                                    <div className="mt-2.5 grid grid-cols-2 lg:grid-cols-4 gap-2 w-full">
                                      <div className="rounded-md bg-white/70 px-2.5 py-2 border border-gray-100/90 min-w-0">
                                        <p className="text-[10px] sm:text-xs font-medium text-gray-600 leading-tight">
                                          Total earnings
                                        </p>
                                        <p className="text-xs sm:text-sm font-semibold text-green-800 mt-0.5 tabular-nums truncate">
                                          {formatSalaryAmountInr(snap.totalEarnings)}
                                        </p>
                                      </div>
                                      <div className="rounded-md bg-white/70 px-2.5 py-2 border border-gray-100/90 min-w-0">
                                        <p className="text-[10px] sm:text-xs font-medium text-gray-600 leading-tight">
                                          Total reimbursements
                                        </p>
                                        <p className="text-xs sm:text-sm font-semibold text-blue-800 mt-0.5 tabular-nums truncate">
                                          {formatSalaryAmountInr(snap.totalReimbursements)}
                                        </p>
                                      </div>
                                      <div className="rounded-md bg-white/70 px-2.5 py-2 border border-gray-100/90 min-w-0">
                                        <p className="text-[10px] sm:text-xs font-medium text-gray-600 leading-tight">
                                          Total deductions
                                        </p>
                                        <p className="text-xs sm:text-sm font-semibold text-red-800 mt-0.5 tabular-nums truncate">
                                          {formatSalaryAmountInr(snap.totalDeductions)}
                                        </p>
                                      </div>
                                      <div className="rounded-md bg-white/70 px-2.5 py-2 border border-gray-100/90 min-w-0">
                                        <p className="text-[10px] sm:text-xs font-medium text-gray-600 leading-tight">
                                          Net pay
                                        </p>
                                        <p className="text-xs sm:text-sm font-semibold text-agrosaf-green mt-0.5 tabular-nums truncate">
                                          {formatSalaryAmountInr(snap.netPay)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                                {isOpen && (
                                  <div className="px-3 pb-3 pt-1 sm:px-4 sm:pb-4 border-t border-gray-100 bg-white/70">
                                    <SalarySnapshotReadOnlyPanels snap={snapForPanels} />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                </>
              ) : !salaryStructureLoading && !salaryStructureError ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm text-gray-700">
                    No salary structure has been saved for this employee.
                  </p>
                </div>
              ) : null}

              {payrollData && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Bank details
                  </h4>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 break-words">
                      <span className="font-medium">Bank Name:</span>{" "}
                      <span className="capitalize">{payrollData.bankName || "N/A"}</span>
                    </p>
                    <p className="text-sm text-gray-600 break-words">
                      <span className="font-medium">Branch Name:</span>{" "}
                      <span className="capitalize">{payrollData.bankBranchName || "N/A"}</span>
                    </p>
                    <p className="text-sm text-gray-600 break-words">
                      <span className="font-medium">Account Number:</span> {payrollData.bankAccount || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 break-words">
                      <span className="font-medium capitalize">IFSC:</span> {payrollData.ifsc || 'N/A'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "exit-request" && exitRequestId && (
            <div className="space-y-6">
              <ExitRequestDetails
                requestId={exitRequestId}
                hideBack={true}
                onBack={() => setActiveTab('personal')}
                onApprove={async (request, role, reason) => {
                  try {
                    const result = await exitService.approveExitRequest(request._id, {
                      approvalRole: role,
                      approvalReason: reason,
                      comments: reason,
                      managerId: user?._id || user?.id,
                      managerName: `${user?.firstName || user?.name || ''} ${user?.lastName || ''}`.trim(),
                      managerEmail: user?.email,
                      managerDepartment: user?.department,
                      managerEmployeeId: user?.employeeId
                    });
                    if (result.success) {
                      toast.success("Exit request approved successfully");
                      queryClient.invalidateQueries({ queryKey: ['exitRequest', request._id] });
                      loadEmployeeData();
                    } else {
                      toast.error(result.error || "Failed to approve exit request");
                    }
                  } catch (err) {

                    toast.error("An error occurred while approving the request");
                  }
                }}
                onReject={async (request, role, reason) => {
                  try {
                    const result = await exitService.rejectExitRequest(request._id, {
                      approvalRole: role,
                      rejectedReason: reason,
                      comments: reason,
                      managerId: user?._id || user?.id,
                      managerName: `${user?.firstName || user?.name || ''} ${user?.lastName || ''}`.trim(),
                      managerEmail: user?.email,
                      managerDepartment: user?.department,
                      managerEmployeeId: user?.employeeId
                    });
                    if (result.success) {
                      toast.success("Exit request rejected successfully");
                      queryClient.invalidateQueries({ queryKey: ['exitRequest', request._id] });
                      loadEmployeeData();
                    } else {
                      toast.error(result.error || "Failed to reject exit request");
                    }
                  } catch (err) {

                    toast.error("An error occurred while rejecting the request");
                  }
                }}
                onWithdraw={async (request) => {
                  try {
                    const result = await exitService.allowWithdrawal(request._id, { allowWithdrawal: true });
                    if (result.success) {
                      toast.success("Withdrawal permission granted");
                      queryClient.invalidateQueries({ queryKey: ['exitRequest', request._id] });
                      loadEmployeeData();
                    } else {
                      toast.error(result.error || "Failed to grant withdrawal permission");
                    }
                  } catch (err) {

                    toast.error("An error occurred");
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {showDocumentPreviewModal && previewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-2 sm:p-4" onClick={() => {
          setShowDocumentPreviewModal(false);
          setPreviewingDocument(null);
        }}>
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {(() => {
                    const FileIcon = getFileIcon(previewingDocument.fileType, previewingDocument.name);
                    return <FileIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />;
                  })()}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    {previewingDocument.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">
                    {previewingDocument.type} • {formatDate(previewingDocument.uploadedDate || previewingDocument.uploadedAt, { includeTime: true }) || previewingDocument.uploadedDate || previewingDocument.uploadedAt || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDocumentPreviewModal(false);
                  setPreviewingDocument(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
              >
                <XCircle className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Document Preview - Main Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {(() => {
                // Get the document URL - prioritize fileUrl, then url
                const docUrl = previewingDocument.fileUrl || (previewingDocument.url && previewingDocument.url !== "#" ? previewingDocument.url : null);
                const fileName = previewingDocument.name?.toLowerCase() || '';
                const fileType = previewingDocument.fileType?.toLowerCase() || '';

                if (!docUrl) {
                  return (
                    <div className="flex flex-col items-center justify-center text-center py-16 text-gray-500 h-full">
                      <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium">Preview not available</p>
                      <p className="text-sm mt-2">This document cannot be previewed. Please download to view.</p>
                      <button
                        onClick={() => handleDownloadDocument(previewingDocument)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Document
                      </button>
                    </div>
                  );
                }

                // Detect file type from extension or fileType field
                const getFileExtension = (name, type) => {
                  if (name) {
                    const match = name.match(/\.([^.]+)$/);
                    if (match) return match[1].toLowerCase();
                  }
                  return type;
                };

                const extension = getFileExtension(fileName, fileType);

                // Image types
                const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'image'];
                const isImage = imageTypes.includes(extension) || imageTypes.includes(fileType);

                // PDF
                const isPDF = extension === 'pdf' || fileType === 'pdf';

                // Text files (can be displayed in browser)
                const textTypes = ['txt', 'csv', 'json', 'xml', 'html', 'css', 'js', 'md', 'log'];
                const isText = textTypes.includes(extension) || textTypes.includes(fileType);

                // Office documents (cannot be previewed in browser, need download)
                const officeTypes = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];
                const isOffice = officeTypes.includes(extension) || officeTypes.includes(fileType);

                // Video types
                const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
                const isVideo = videoTypes.includes(extension) || videoTypes.includes(fileType);

                // Audio types
                const audioTypes = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'];
                const isAudio = audioTypes.includes(extension) || audioTypes.includes(fileType);

                // Render based on file type
                if (isImage) {
                  return (
                    <div className="flex items-center justify-center bg-white rounded-lg p-4 min-h-[500px] h-full">
                      <img
                        src={docUrl}
                        alt={previewingDocument.name}
                        className="max-w-full max-h-[75vh] w-auto h-auto rounded-lg border border-gray-200 shadow-lg object-contain"
                        onError={(e) => {

                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextElementSibling;
                          if (errorDiv) {
                            errorDiv.style.display = 'flex';
                          }
                        }}
                        onLoad={() => {

                        }}
                      />
                      <div style={{ display: 'none' }} className="flex flex-col items-center justify-center text-center py-12 text-gray-500">
                        <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">Preview not available</p>
                        <p className="text-sm mt-2">Please download to view the document</p>
                        <button
                          onClick={() => handleDownloadDocument(previewingDocument)}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Document
                        </button>
                      </div>
                    </div>
                  );
                } else if (isPDF) {
                  return (
                    <div className="w-full h-[75vh] bg-white rounded-lg overflow-hidden border border-gray-200 shadow-lg">
                      <iframe
                        src={`${docUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full h-full border-0"
                        title={previewingDocument.name}
                        onError={() => {

                        }}
                      />
                    </div>
                  );
                } else if (isText) {
                  // For text files, try to display content
                  return (
                    <div className="w-full h-[75vh] bg-white rounded-lg overflow-hidden border border-gray-200 shadow-lg flex flex-col">
                      <div className="flex-1 overflow-auto p-4">
                        <iframe
                          src={docUrl}
                          className="w-full h-full border-0"
                          title={previewingDocument.name}
                          onError={() => {

                          }}
                        />
                      </div>
                    </div>
                  );
                } else if (isVideo) {
                  return (
                    <div className="w-full h-[75vh] bg-black rounded-lg overflow-hidden border border-gray-200 shadow-lg flex items-center justify-center">
                      <video
                        src={docUrl}
                        controls
                        className="max-w-full max-h-full"
                        onError={(e) => {

                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  );
                } else if (isAudio) {
                  return (
                    <div className="w-full h-[75vh] bg-white rounded-lg overflow-hidden border border-gray-200 shadow-lg flex items-center justify-center">
                      <div className="text-center p-8">
                        <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                        <audio
                          src={docUrl}
                          controls
                          className="w-full max-w-md"
                          onError={(e) => {

                          }}
                        >
                          Your browser does not support the audio tag.
                        </audio>
                        <p className="mt-4 text-sm text-gray-600">{previewingDocument.name}</p>
                      </div>
                    </div>
                  );
                } else if (isOffice) {
                  // Office documents cannot be previewed in browser
                  return (
                    <div className="flex flex-col items-center justify-center text-center py-16 text-gray-500 h-full bg-white rounded-lg">
                      <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium">Preview not available</p>
                      <p className="text-sm mt-2 mb-4">
                        This document type ({extension.toUpperCase() || fileType.toUpperCase()}) cannot be previewed in the browser.
                      </p>
                      <p className="text-sm text-gray-400 mb-6">
                        Please download the file to view it using the appropriate application.
                      </p>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            window.open(docUrl, '_blank');
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Open in New Tab
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(previewingDocument)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Document
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  // For other file types, try iframe first, fallback to download option
                  return (
                    <div className="w-full h-[75vh] bg-white rounded-lg overflow-hidden border border-gray-200 shadow-lg flex flex-col">
                      <div className="flex-1 overflow-auto">
                        <iframe
                          src={docUrl}
                          className="w-full h-full border-0"
                          title={previewingDocument.name}
                          onError={() => {

                          }}
                        />
                      </div>
                      <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <p className="text-sm text-gray-600 mb-3">
                          If the preview doesn't load, you can download the file to view it.
                        </p>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => {
                              window.open(docUrl, '_blank');
                            }}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Open in New Tab
                          </button>
                          <button
                            onClick={() => handleDownloadDocument(previewingDocument)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Document
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Footer with Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-t border-gray-200 bg-gray-50 gap-3 flex-shrink-0">
              <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                {previewingDocument.size && (
                  <span className="break-words">Size: {previewingDocument.size}</span>
                )}
                {previewingDocument.fileType && (
                  <span className="break-words">Type: {previewingDocument.fileType.toUpperCase()}</span>
                )}
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusInfo(previewingDocument.status).color
                    }`}
                >
                  {(() => {
                    const StatusIcon = getStatusInfo(previewingDocument.status).icon;
                    return (
                      <>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {previewingDocument.status}
                      </>
                    );
                  })()}
                </span>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setShowDocumentPreviewModal(false);
                    setPreviewingDocument(null);
                  }}
                  className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm flex-1 sm:flex-initial"
                >
                  Close
                </button>
                <PermissionGate permission="employee_management" action="read">
                  <button
                    onClick={() => handleDownloadDocument(previewingDocument)}
                    className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm flex-1 sm:flex-initial"
                  >
                    <Download className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Download</span>
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;
